import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApprovalWorkflow, useDeleteConfirmState } from '@/components/hooks';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, FileText, Zap, Plus, LayoutGrid, TableProperties, Archive } from 'lucide-react';
import { format } from 'date-fns';
import IssueCard from '@/components/issues/IssueCard';
import PolicyCard from '@/components/issues/PolicyCard';
import CapabilityCard from '@/components/issues/CapabilityCard';
import IssueSearch from '@/components/issues/IssueSearch';
import IssueTableView from '@/components/issues/IssueTableView';
import PolicyTableView from '@/components/issues/PolicyTableView';
import CapabilityTableView from '@/components/issues/CapabilityTableView';
import IssueDialog from '@/components/issues/IssueDialog';
import PolicyDialog from '@/components/issues/PolicyDialog';
import CapabilityDialog from '@/components/issues/CapabilityDialog';
import ExistingPolicyDialog from '@/components/issues/ExistingPolicyDialog';
import ExistingCapabilityDialog from '@/components/issues/ExistingCapabilityDialog';
import IssueScanDialog from '@/components/issues/IssueScanDialog';
import PolicySearch from '@/components/issues/PolicySearch';
import CapabilitySearch from '@/components/issues/CapabilitySearch';

export default function IssuesPage() {
  const queryClient = useQueryClient();
  const [issueDialog, setIssueDialog] = useState(false);
  const [policyDialog, setPolicyDialog] = useState(false);
  const [capabilityDialog, setCapabilityDialog] = useState(false);
  const [existingPolicyDialog, setExistingPolicyDialog] = useState(false);
  const [existingCapabilityDialog, setExistingCapabilityDialog] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [editingCapability, setEditingCapability] = useState(null);
  const [scanDialog, setScanDialog] = useState(false);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [viewMode, setViewMode] = useState('card');
  const [triageFilter, setTriageFilter] = useState('all');
  const [policyViewMode, setPolicyViewMode] = useState('card');
  const [capabilityViewMode, setCapabilityViewMode] = useState('card');
  const [showRetiredPolicies, setShowRetiredPolicies] = useState(false);
  const [showDeprecatedCapabilities, setShowDeprecatedCapabilities] = useState(false);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [filteredCapabilities, setFilteredCapabilities] = useState([]);
  const deleteConfirm = useDeleteConfirmState({ confirmTimeout: 3000 });
  const [activeTab, setActiveTab] = useState('issues');
  const deepLinkHandled = useRef(false);

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const { data: profile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find((p) => p.user_email === user?.email);
    },
    enabled: !!user
  });

  const isSeniorOfficer = user?.role === 'admin' || profile?.custom_role === 'Commander' || profile?.custom_role === 'Commander in Chief' || profile?.custom_role === 'Officer';
  const isCommander = user?.role === 'admin' || profile?.custom_role === 'Commander' || profile?.custom_role === 'Commander in Chief';

  const policyWorkflow = useApprovalWorkflow({
    entityType: 'Policy',
    userEmail: user?.email
  });

  const capabilityWorkflow = useApprovalWorkflow({
    entityType: 'Capability',
    userEmail: user?.email
  });

  const { data: officers } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    initialData: []
  });

  const { data: engines } = useQuery({
    queryKey: ['engines'],
    queryFn: () => base44.entities.Engine.list(),
    initialData: []
  });

  const { data: missions } = useQuery({
    queryKey: ['missions'],
    queryFn: () => base44.entities.Mission.list(),
    initialData: []
  });

  const { data: objectives } = useQuery({
    queryKey: ['objectives'],
    queryFn: () => base44.entities.Objective.list(),
    initialData: []
  });

  const { data: issues } = useQuery({
    queryKey: ['issues'],
    queryFn: () => base44.entities.Issue.list(),
    initialData: []
  });

  const { data: reportIssues } = useQuery({
    queryKey: ['reportIssues'],
    queryFn: () => base44.entities.ReportIssue.list(),
    initialData: []
  });

  const { data: progressReports } = useQuery({
    queryKey: ['progressReports'],
    queryFn: () => base44.entities.ProgressReport.list(),
    initialData: []
  });

  const { data: activities } = useQuery({
    queryKey: ['activities'],
    queryFn: () => base44.entities.Activity.list(),
    initialData: []
  });

  const { data: policies } = useQuery({
    queryKey: ['policies'],
    queryFn: () => base44.entities.Policy.list(),
    initialData: []
  });

  const { data: capabilities } = useQuery({
    queryKey: ['capabilities'],
    queryFn: () => base44.entities.Capability.list(),
    initialData: []
  });

  const { data: artifacts } = useQuery({
    queryKey: ['artifacts'],
    queryFn: () => base44.entities.Artifact.list(),
    initialData: []
  });

  const { data: rituals } = useQuery({
    queryKey: ['rituals'],
    queryFn: () => base44.entities.Ritual.list(),
    initialData: []
  });

  // Deep-link: read ?tab=...&highlight=... from URL and open the correct item
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    const highlightId = urlParams.get('highlight');
    if (!tab || !highlightId) return;

    if (tab === 'issues' && issues.length > 0) {
      setActiveTab('issues');
      const found = issues.find(i => i.id === highlightId);
      if (found) {
        setEditingIssue(found);
        setIssueDialog(true);
        deepLinkHandled.current = true;
      }
    } else if (tab === 'policies' && policies.length > 0) {
      setActiveTab('policies');
      const found = policies.find(p => p.id === highlightId);
      if (found) {
        setEditingPolicy(found);
        setPolicyDialog(true);
        deepLinkHandled.current = true;
      }
    } else if (tab === 'capabilities' && capabilities.length > 0) {
      setActiveTab('capabilities');
      const found = capabilities.find(c => c.id === highlightId);
      if (found) {
        setEditingCapability(found);
        setCapabilityDialog(true);
        deepLinkHandled.current = true;
      }
    }
  }, [issues, policies, capabilities]);

  const handleEditIssue = (issue) => {
    setEditingIssue(issue);
    setIssueDialog(true);
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyDialog(true);
  };

  const handleEditCapability = (capability) => {
    setEditingCapability(capability);
    setCapabilityDialog(true);
  };

  const deleteIssueMutation = useMutation({
    mutationFn: (id) => base44.entities.Issue.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['issues'] })
  });

  const deleteReportIssueMutation = useMutation({
    mutationFn: async (id) => {
      // Mark as removed instead of deleting
      await base44.entities.ReportIssue.update(id, {
        removed_from_list: {
          removed_by: user?.email,
          removed_at: new Date().toISOString()
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportIssues'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    }
  });

  const reissueReportIssueMutation = useMutation({
    mutationFn: async (id) => {
      // Remove the removal marker and log audit trail
      await base44.entities.ReportIssue.update(id, {
        removed_from_list: null,
        reissued_by: user?.email,
        reissued_at: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportIssues'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    }
  });

  const archiveIssueMutation = useMutation({
    mutationFn: (id) => base44.entities.Issue.update(id, { status: 'archived' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['issues'] })
  });

  const unarchiveIssueMutation = useMutation({
    mutationFn: (id) => base44.entities.Issue.update(id, { status: 'open' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['issues'] })
  });

  const retirePolicyMutation = useMutation({
    mutationFn: (id) => base44.entities.Policy.update(id, { status: 'retired', retired_at: new Date().toISOString(), retired_by: user?.email }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['policies'] })
  });

  const unretirePolicyMutation = useMutation({
    mutationFn: (id) => base44.entities.Policy.update(id, { status: 'draft', retired_at: null, retired_by: null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['policies'] })
  });

  const deletePolicyMutation = useMutation({
    mutationFn: (id) => base44.entities.Policy.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['policies'] })
  });

  const deprecateCapabilityMutation = useMutation({
    mutationFn: (id) => base44.entities.Capability.update(id, { status: 'deprecated' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['capabilities'] })
  });

  const undeprecateCapabilityMutation = useMutation({
    mutationFn: (id) => base44.entities.Capability.update(id, { status: 'requested' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['capabilities'] })
  });

  const deleteCapabilityMutation = useMutation({
    mutationFn: (id) => base44.entities.Capability.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['capabilities'] })
  });

  // Combine manual issues and report issues (only from APPROVED reports)
  // Group report issues by activity_id + description to avoid duplicates
  const approvedReportIssues = reportIssues.filter(reportIssue => {
    if (reportIssue.removed_from_list) return false;
    const report = progressReports.find(r => r.id === reportIssue.progress_report_id);
    return report?.approved === true;
  });

  const groupedReportIssues = new Map();
  approvedReportIssues.forEach(reportIssue => {
    const report = progressReports.find(r => r.id === reportIssue.progress_report_id);
    const activity = report ? activities.find(a => a.id === report.activity_id) : null;
    const key = `${report?.activity_id}_${reportIssue.issue_description}`;
    
    if (!groupedReportIssues.has(key)) {
      groupedReportIssues.set(key, {
        reportIssue,
        report,
        activity,
        relatedIssues: []
      });
    }
    groupedReportIssues.get(key).relatedIssues.push(reportIssue);
  });

  const combinedIssues = [
    ...issues.map(issue => ({
      ...issue,
      displayType: issue.source ? issue.source.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Manual'
    })),
    ...Array.from(groupedReportIssues.values()).map(({ reportIssue, report, activity, relatedIssues }) => {
      const removedTags = reportIssue.removed_tags || [];
      const hasActiveTags = 
        (reportIssue.tagged_officers || []).some(tag => !removedTags.some(rt => rt.tag_type === 'tagged_officers' && rt.tag_value === tag)) ||
        (reportIssue.tagged_artifacts || []).some(tag => !removedTags.some(rt => rt.tag_type === 'tagged_artifacts' && rt.tag_value === tag)) ||
        (reportIssue.tagged_rituals || []).some(tag => !removedTags.some(rt => rt.tag_type === 'tagged_rituals' && rt.tag_value === tag)) ||
        (reportIssue.tagged_policies || []).some(tag => !removedTags.some(rt => rt.tag_type === 'tagged_policies' && rt.tag_value === tag)) ||
        (reportIssue.tagged_capabilities || []).some(tag => !removedTags.some(rt => rt.tag_type === 'tagged_capabilities' && rt.tag_value === tag));
      
      // Build timestamps string from all related issues
      const timestamps = relatedIssues.map(ri => {
        const r = progressReports.find(pr => pr.id === ri.progress_report_id);
        return format(new Date(r.reported_at), 'MMM d, yyyy HH:mm');
      }).join(', ');
      
      return {
        id: reportIssue.id,
        title: reportIssue.issue_description?.substring(0, 60) + (reportIssue.issue_description?.length > 60 ? '...' : ''),
        description: reportIssue.issue_description,
        category: 'execution_headwind',
        source: 'activity',
        displayType: 'Activity',
        reported_by: report?.reported_by,
        reported_at: report?.reported_at,
        reported_timestamps: timestamps,
        activity_code: activity?.activity_code,
        activity_id: report?.activity_id,
        progress_report_id: reportIssue.progress_report_id,
        status: hasActiveTags ? 'open' : 'resolved',
        tagged_officers: reportIssue.tagged_officers || [],
        tagged_artifacts: reportIssue.tagged_artifacts || [],
        tagged_rituals: reportIssue.tagged_rituals || [],
        tagged_policies: reportIssue.tagged_policies || [],
        tagged_capabilities: reportIssue.tagged_capabilities || [],
        removed_tags: reportIssue.removed_tags || []
      };
    })
  ].sort((a, b) => new Date(b.reported_at || b.created_date) - new Date(a.reported_at || a.created_date));

  const openIssueCount = combinedIssues.filter((i) => i.status === 'open').length;
  const activePoliciesCount = policies.filter((p) => p.status === 'active').length;
  const activeCapabilitiesCount = capabilities.filter((c) => c.status === 'active').length;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-2">Issues, Policies & Capabilities</h1>
        <p className="text-slate-400">
          Manage organizational challenges, design policies, and develop capabilities
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass-panel border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Open Issues</p>
                <p className="text-3xl font-bold text-red-400">{openIssueCount}</p>
              </div>
              <AlertTriangle className="w-12 h-12 text-red-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Active Policies</p>
                <p className="text-3xl font-bold text-amber-400">{activePoliciesCount}</p>
              </div>
              <FileText className="w-12 h-12 text-amber-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Active Capabilities</p>
                <p className="text-3xl font-bold text-emerald-400">{activeCapabilitiesCount}</p>
              </div>
              <Zap className="w-12 h-12 text-emerald-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-900/50 border border-slate-800">
          <TabsTrigger value="issues">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Issues
          </TabsTrigger>
          <TabsTrigger value="policies">
            <FileText className="w-4 h-4 mr-2" />
            Policies
          </TabsTrigger>
          <TabsTrigger value="capabilities">
            <Zap className="w-4 h-4 mr-2" />
            Capabilities
          </TabsTrigger>
        </TabsList>

        {/* ISSUES TAB */}
        <TabsContent value="issues" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-slate-400 text-sm">
              Track and manage issues arising from activities and tasks
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => setScanDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Scan for Issues
              </Button>
              <Button
                onClick={() => {
                  setEditingIssue(null);
                  setIssueDialog(true);
                }}
                className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Report Issue
              </Button>
            </div>
            </div>

            {/* Triage Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'pending', label: 'Pending Triage' },
                { key: 'noise', label: 'Noise Archive' },
                { key: 'signals', label: 'Signals' },
                { key: 'balloons', label: 'Balloons 🎈' },
                { key: 'rocks', label: 'Rocks 🪨' }
              ].map(f => (
                <Button
                  key={f.key}
                  size="sm"
                  variant={triageFilter === f.key ? 'default' : 'outline'}
                  onClick={() => setTriageFilter(f.key)}
                  className={`h-7 text-xs ${triageFilter === f.key 
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                    : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>

            <IssueSearch
            issues={combinedIssues.filter(issue => {
              if (triageFilter === 'all') return true;
              if (triageFilter === 'pending') return !issue.classification_gate1 || issue.classification_gate1 === 'pending';
              if (triageFilter === 'noise') return issue.classification_gate1 === 'noise';
              if (triageFilter === 'signals') return issue.classification_gate1 === 'signal';
              if (triageFilter === 'balloons') return issue.classification_gate2 === 'balloon';
              if (triageFilter === 'rocks') return issue.classification_gate2 === 'rock';
              return true;
            })}
            officers={officers}
            missions={missions}
            objectives={objectives}
            activities={activities}
            artifacts={artifacts}
            rituals={rituals}
            policies={policies}
            capabilities={capabilities}
            onFilteredResults={setFilteredIssues}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            />

            {viewMode === 'card' ? (
              <div className="grid grid-cols-1 gap-4">
                {filteredIssues.map((issue) =>
                <IssueCard
                  key={`${issue.source}-${issue.id}`}
                  issue={issue}
                  officers={officers}
                  engines={engines}
                  missions={missions}
                  objectives={objectives}
                  policies={policies}
                  capabilities={capabilities}
                  activities={activities}
                  onEdit={handleEditIssue}
                  onDelete={issue.progress_report_id ? deleteReportIssueMutation : deleteIssueMutation}
                  onArchive={!issue.progress_report_id ? archiveIssueMutation : null}
                  onUnarchive={!issue.progress_report_id ? unarchiveIssueMutation : null}
                  isSeniorOfficer={isSeniorOfficer} />
                )}
              </div>
            ) : (
              <IssueTableView
                issues={filteredIssues}
                officers={officers}
                missions={missions}
                objectives={objectives}
                activities={activities}
                policies={policies}
                capabilities={capabilities}
                onEdit={handleEditIssue}
                onDelete={deleteIssueMutation}
                onDeleteReportIssue={deleteReportIssueMutation}
                onArchive={archiveIssueMutation}
                onUnarchive={unarchiveIssueMutation}
              />
            )}

            {filteredIssues.length === 0 && combinedIssues.length > 0 && (
            <Card className="glass-panel border-slate-800">
              <CardContent className="py-12 text-center">
                <AlertTriangle className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-2">No issues match your filters</p>
                <p className="text-sm text-slate-600">Try adjusting your search or filters</p>
              </CardContent>
            </Card>
            )}

            {combinedIssues.length === 0 &&
          <Card className="glass-panel border-slate-800">
              <CardContent className="py-12 text-center">
                <AlertTriangle className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-2">No issues reported</p>
                <p className="text-sm text-slate-600">Issues will appear here when reported from activities or manually added</p>
              </CardContent>
            </Card>
          }
        </TabsContent>

        {/* POLICIES TAB */}
        <TabsContent value="policies" className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <p className="text-slate-400 text-sm">
                {policies.length} polic{policies.length !== 1 ? 'ies' : 'y'}
              </p>
              <Button
                variant={showRetiredPolicies ? "default" : "outline"}
                size="sm"
                onClick={() => setShowRetiredPolicies(!showRetiredPolicies)}
                className={`h-7 text-xs ${showRetiredPolicies ? 'bg-amber-600 hover:bg-amber-700' : 'border-slate-700 text-slate-400'}`}
              >
                <Archive className="w-3 h-3 mr-1" />
                Retired ({policies.filter(p => p.status === 'retired').length})
              </Button>
              <div className="flex items-center border border-slate-700 rounded-md overflow-hidden">
                <button
                  onClick={() => setPolicyViewMode('card')}
                  className={`p-1.5 transition-colors ${policyViewMode === 'card' ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}
                  title="Card view"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPolicyViewMode('table')}
                  className={`p-1.5 transition-colors ${policyViewMode === 'table' ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}
                  title="Table view"
                >
                  <TableProperties className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setExistingPolicyDialog(true)}
                variant="outline" className="bg-indigo-600 px-4 py-2 text-sm font-medium rounded-md inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border shadow-sm hover:bg-accent hover:text-accent-foreground h-9 border-slate-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Existing Policy
              </Button>
              <Button
                onClick={() => {
                  setEditingPolicy(null);
                  setPolicyDialog(true);
                }} className="bg-amber-500 text-primary-foreground px-4 py-2 text-sm font-medium rounded-md inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 shadow h-9 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Design Policy
              </Button>
            </div>
          </div>

          <PolicySearch
            policies={policies.filter(p => showRetiredPolicies ? p.status === 'retired' : p.status !== 'retired')}
            officers={officers}
            onFilteredResults={setFilteredPolicies}
          />

          {policyViewMode === 'table' ? (
            <PolicyTableView
              policies={filteredPolicies}
              officers={officers}
              capabilities={capabilities}
              user={user}
              isSeniorOfficer={isSeniorOfficer}
              onEdit={handleEditPolicy}
              onDelete={(id) => deletePolicyMutation.mutate(id)}
              onApprove={(id) => policyWorkflow.approve(id)}
              onInstitutionalize={(id) => policyWorkflow.institutionalize(id)}
              onArchive={(id) => retirePolicyMutation.mutate(id)}
              onUnarchive={(id) => unretirePolicyMutation.mutate(id)}
              onRetire={(id) => retirePolicyMutation.mutate(id)}
              deleteConfirm={deleteConfirm}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredPolicies.map((policy) =>
              <PolicyCard
                key={policy.id}
                policy={policy}
                officers={officers}
                issues={issues}
                capabilities={capabilities}
                onEdit={handleEditPolicy}
                isSeniorOfficer={isSeniorOfficer}
                onApprove={(id) => policyWorkflow.approve(id)}
                onInstitutionalize={(id) => policyWorkflow.institutionalize(id)}
                onArchive={(id) => retirePolicyMutation.mutate(id)}
                onUnarchive={(id) => unretirePolicyMutation.mutate(id)} />
              )}
            </div>
          )}

          {policies.length === 0 &&
          <Card className="glass-panel border-slate-800">
              <CardContent className="py-12 text-center">
                <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-2">No policies yet</p>
                <p className="text-sm text-slate-600">Design policies to address organizational issues</p>
              </CardContent>
            </Card>
          }
        </TabsContent>

        {/* CAPABILITIES TAB */}
        <TabsContent value="capabilities" className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <p className="text-slate-400 text-sm">
                {capabilities.length} capabilit{capabilities.length !== 1 ? 'ies' : 'y'}
              </p>
              <Button
                variant={showDeprecatedCapabilities ? "default" : "outline"}
                size="sm"
                onClick={() => setShowDeprecatedCapabilities(!showDeprecatedCapabilities)}
                className={`h-7 text-xs ${showDeprecatedCapabilities ? 'bg-amber-600 hover:bg-amber-700' : 'border-slate-700 text-slate-400'}`}
              >
                <Archive className="w-3 h-3 mr-1" />
                Deprecated ({capabilities.filter(c => c.status === 'deprecated').length})
              </Button>
              <div className="flex items-center border border-slate-700 rounded-md overflow-hidden">
                <button
                  onClick={() => setCapabilityViewMode('card')}
                  className={`p-1.5 transition-colors ${capabilityViewMode === 'card' ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}
                  title="Card view"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCapabilityViewMode('table')}
                  className={`p-1.5 transition-colors ${capabilityViewMode === 'table' ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}
                  title="Table view"
                >
                  <TableProperties className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setExistingCapabilityDialog(true)}
                variant="outline" className="bg-indigo-600 px-4 py-2 text-sm font-medium rounded-md inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border shadow-sm hover:bg-accent hover:text-accent-foreground h-9 border-slate-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Existing Capability
              </Button>
              <Button
                onClick={() => {
                  setEditingCapability(null);
                  setCapabilityDialog(true);
                }} className="bg-green-600 text-primary-foreground px-4 py-2 text-sm font-medium rounded-md inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 shadow h-9 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Request Capability
              </Button>
            </div>
          </div>

          <CapabilitySearch
            capabilities={capabilities.filter(c => showDeprecatedCapabilities ? c.status === 'deprecated' : c.status !== 'deprecated')}
            officers={officers}
            onFilteredResults={setFilteredCapabilities}
          />

          {capabilityViewMode === 'table' ? (
            <CapabilityTableView
              capabilities={filteredCapabilities}
              officers={officers}
              user={user}
              isSeniorOfficer={isSeniorOfficer}
              onEdit={handleEditCapability}
              onDelete={(id) => deleteCapabilityMutation.mutate(id)}
              onApprove={(id) => capabilityWorkflow.approve(id)}
              onInstitutionalize={(id) => capabilityWorkflow.institutionalize(id)}
              onArchive={(id) => deprecateCapabilityMutation.mutate(id)}
              onUnarchive={(id) => undeprecateCapabilityMutation.mutate(id)}
              deleteConfirm={deleteConfirm}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredCapabilities.map((capability) =>
              <CapabilityCard
                key={capability.id}
                capability={capability}
                officers={officers}
                issues={issues}
                policies={policies}
                objectives={objectives}
                onEdit={handleEditCapability}
                isSeniorOfficer={isSeniorOfficer}
                onApprove={(id) => capabilityWorkflow.approve(id)}
                onInstitutionalize={(id) => capabilityWorkflow.institutionalize(id)}
                onArchive={(id) => deprecateCapabilityMutation.mutate(id)}
                onUnarchive={(id) => undeprecateCapabilityMutation.mutate(id)} />
              )}
            </div>
          )}

          {capabilities.length === 0 &&
          <Card className="glass-panel border-slate-800">
              <CardContent className="py-12 text-center">
                <Zap className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-2">No capabilities yet</p>
                <p className="text-sm text-slate-600">Request or develop capabilities to address issues</p>
              </CardContent>
            </Card>
          }
        </TabsContent>
      </Tabs>

      <IssueDialog
        open={issueDialog}
        onClose={() => {
          setIssueDialog(false);
          setEditingIssue(null);
        }}
        issue={editingIssue}
        officers={officers}
        missions={missions}
        objectives={objectives}
        artifacts={artifacts}
        rituals={rituals}
        policies={policies}
        capabilities={capabilities}
        activities={activities}
        isCommander={isCommander} />


      <PolicyDialog
        open={policyDialog}
        onClose={() => {
          setPolicyDialog(false);
          setEditingPolicy(null);
        }}
        policy={editingPolicy}
        officers={officers}
        issues={issues}
        capabilities={capabilities} />


      <CapabilityDialog
        open={capabilityDialog}
        onClose={() => {
          setCapabilityDialog(false);
          setEditingCapability(null);
        }}
        capability={editingCapability}
        officers={officers}
        issues={issues}
        policies={policies}
        objectives={objectives} />


      <ExistingPolicyDialog
        open={existingPolicyDialog}
        onClose={() => setExistingPolicyDialog(false)} />


      <ExistingCapabilityDialog
        open={existingCapabilityDialog}
        onClose={() => setExistingCapabilityDialog(false)}
        officers={officers} />

      <IssueScanDialog
        open={scanDialog}
        onClose={() => setScanDialog(false)} />

      </div>);

}